/**
 * Edit BeatStars sell URL, price, and collaborator for a file (English UI).
 */
import React, { useState, useEffect } from 'react';
import { X, Save, ExternalLink } from 'lucide-react';
import { AudioFile } from '@types';
import { useFileStore } from '@stores/useFileStore';
import { useToast } from '@components/ui';

interface EditSellingInfoModalProps {
  file: AudioFile;
  onClose: () => void;
  onSuccess?: () => void;
}

export const EditSellingInfoModal: React.FC<EditSellingInfoModalProps> = ({
  file,
  onClose,
  onSuccess,
}) => {
  const [beatstarsSellUrl, setBeatstarsSellUrl] = useState(file.beatstarsSellUrl ?? '');
  const [beatstarsPrice, setBeatstarsPrice] = useState(file.beatstarsPrice ?? '');
  const [collaborator, setCollaborator] = useState(file.collaborator ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const { updateFile } = useFileStore();
  const { success, error: showError } = useToast();

  useEffect(() => {
    setBeatstarsSellUrl(file.beatstarsSellUrl ?? '');
    setBeatstarsPrice(file.beatstarsPrice ?? '');
    setCollaborator(file.collaborator ?? '');
  }, [file.id, file.beatstarsSellUrl, file.beatstarsPrice, file.collaborator]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateFile(file.id, {
        beatstarsSellUrl: beatstarsSellUrl.trim() || undefined,
        beatstarsPrice: beatstarsPrice.trim() || undefined,
        collaborator: collaborator.trim() || undefined,
      });
      success('Saved', 'BeatStars & collaborator updated.');
      onSuccess?.();
      onClose();
    } catch (e: any) {
      showError('Error', e.message || 'Failed to save.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-dark-secondary border border-white/10 rounded-xl shadow-xl max-w-md w-full p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-dela text-lg text-white flex items-center gap-2">
            <ExternalLink className="w-4 h-4 text-primary" />
            BeatStars &amp; collaborator
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-white/60 font-inter text-sm mb-4 truncate">
          {file.name}
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-inter font-medium text-white/70 mb-1">BeatStars sell URL</label>
            <input
              type="url"
              value={beatstarsSellUrl}
              onChange={(e) => setBeatstarsSellUrl(e.target.value)}
              placeholder="https://www.beatstars.com/beat/..."
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm font-inter placeholder:text-white/40 focus:outline-none focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-inter font-medium text-white/70 mb-1">Price</label>
            <input
              type="text"
              value={beatstarsPrice}
              onChange={(e) => setBeatstarsPrice(e.target.value)}
              placeholder="e.g. 49 € or $29.99"
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm font-inter placeholder:text-white/40 focus:outline-none focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-inter font-medium text-white/70 mb-1">Collaborator</label>
            <input
              type="text"
              value={collaborator}
              onChange={(e) => setCollaborator(e.target.value)}
              placeholder="Name or handle"
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm font-inter placeholder:text-white/40 focus:outline-none focus:border-primary"
            />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-3 py-2 text-sm bg-white/10 hover:bg-white/20 text-white rounded-lg font-inter font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 px-3 py-2 text-sm bg-primary hover:bg-primary/90 text-black rounded-lg font-inter font-bold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" />
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};
